import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { CloseIconSmall } from "@whoosh/design-system/src/components/icons_v2/general";
import { RainIcon } from "@whoosh/design-system/src/components/icons_v2/weather";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";
import { convertServerTimeString } from "@whoosh/utilities/src/dates";

import { BookingStatus } from "../../__generated__/BookingCredits_booking.graphql";
import { BookingPaymentRaincheck_Raincheck$key } from "./__generated__/BookingPaymentRaincheck_Raincheck.graphql";

type Props = {
  raincheckRef: BookingPaymentRaincheck_Raincheck$key | null;
  bookingStatus?: BookingStatus;
  onRemove: (raincheckId: string) => void;
};

export const BookingPaymentRaincheck = ({
  raincheckRef,
  bookingStatus,
  onRemove,
}: Props) => {
  const raincheck = useFragment(
    BookingPaymentRaincheckFragmentString,
    raincheckRef
  );
  const { currencySymbol } = useGetClubCurrency();

  const createdAt = raincheck?.createdAt;

  const createdAtFormatted = convertServerTimeString(createdAt, "MMM do, yyyy");

  return (
    <Box
      p={3}
      borderRadius="2"
      backgroundColor={"surface-primary"}
      borderWidth={4}
      borderColor={"surface-secondary"}
      gap={2}
    >
      <HStack
        bg="surface-primary"
        width="100%"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box borderRadius="2" flexDirection="row" gap={2} alignItems="center">
          <Box
            bg="surface-secondary"
            borderRadius="2"
            height={26}
            width={40}
            justifyContent="center"
            alignItems="center"
          >
            <RainIcon />
          </Box>
          <VStack>
            <Text>{createdAtFormatted ?? t`Raincheck`}</Text>
            <Text
              variant="mainTiny"
              color="text-secondary"
            >{t`Balance: ${currencySymbol}${ConvertCentsToDollars(
              raincheck?.squareGiftCard?.balance ?? 0
            )}`}</Text>
          </VStack>
        </Box>

        {bookingStatus === "DRAFT" ? (
          <Button
            accessibilityLabel={t`Remove Raincheck`}
            variant="danger"
            size="tiny"
            leftIcon={<CloseIconSmall />}
            onPress={() => onRemove(raincheck?.id ?? "")}
          />
        ) : null}
      </HStack>
    </Box>
  );
};

export const BookingPaymentRaincheckFragmentString = graphql`
  fragment BookingPaymentRaincheck_Raincheck on Raincheck {
    id
    createdAt
    squareGiftCard {
      id
      balance
    }
  }
`;
